<template>
    <div class="gaylord-page">
        <div class="floatingContact">
            <button class="floating-request-btn" @click="openQuoteModal">Request A Free Quote</button>
            <ContactForm ref="contactForm" :hideButtons="true" />
        </div>

        <section class="hero-section">
            <p class="eyebrow">Gaylord, Michigan Roofing Services</p>
            <h1>Trusted Roof Repair and Roofing Services in Gaylord, Michigan</h1>
            <p class="hero-intro">
                Homes Roofing Co helps Gaylord homeowners protect their homes with dependable roof repair,
                roof replacement, fascia and soffit work, gutters, siding, and free inspections. If you need
                fast help after storm damage or you are planning a full exterior upgrade, our team is ready to help.
            </p>

            <div class="hero-actions">
                <a href="tel:269-262-5055" class="cta-phone-btn">📞 Call 269-262-5055</a>
                <button class="cta-quote-btn" @click="openQuoteModal">Request A Free Quote</button>
            </div>

            <ul class="trust-points">
                <li>Licensed and insured Michigan roofing contractor</li>
                <li>Emergency roof repair available</li>
                <li>50-year warranty options available</li>
                <li>Financing available on qualifying projects</li>
            </ul>
        </section>

        <section class="content-section">
            <h2>Roofing Services Available in Gaylord</h2>
            <div class="service-grid">
                <article v-for="service in services" :key="service.title" class="service-card">
                    <h3>{{ service.title }}</h3>
                    <p>{{ service.description }}</p>
                </article>
            </div>
        </section>

        <section class="content-section weather-section">
            <h2>Built for Gaylord Weather</h2>
            <p>
                Homes in Gaylord deal with heavy snow, wind-driven rain, freeze-thaw cycles, and ice dam risks.
                We recommend roofing systems and exterior materials that are built to handle Michigan weather while
                protecting your home from leaks, heat loss, and long-term moisture damage.
            </p>
            <p>
                Whether you need a quick repair after a storm or a full replacement before the next winter season,
                we focus on durable workmanship, clear communication, and long-term value.
            </p>
        </section>

        <section class="content-section">
            <h2>Why Gaylord Homeowners Choose H.O.M.E.S. Roofing Co.</h2>
            <ul class="benefits-list">
                <li v-for="benefit in benefits" :key="benefit">{{ benefit }}</li>
            </ul>
        </section>

        <section class="content-section">
            <h2>Explore More of Our Work</h2>
            <p class="section-intro">
                See examples of our recent roofing and exterior work, then reach out for a free estimate for your Gaylord home.
            </p>
            <div class="link-grid">
                <router-link v-for="link in galleryLinks" :key="link.to" :to="link.to" class="link-card">
                    <h3>{{ link.title }}</h3>
                    <p>{{ link.description }}</p>
                    <span>View Page →</span>
                </router-link>
            </div>
        </section>

        <section class="content-section faq-wrapper">
            <h2>Frequently Asked Questions About Gaylord Roofing</h2>
            <div class="faq-section">
                <article v-for="item in faqs" :key="item.question" class="faq-item">
                    <h3>{{ item.question }}</h3>
                    <p>{{ item.answer }}</p>
                </article>
            </div>
        </section>

        <section class="content-section cta-section">
            <h2>Need a Roofing Estimate in Gaylord?</h2>
            <p>
                If you are dealing with a leak, missing shingles, storm damage, or an aging roof,
                Homes Roofing Co is ready to help. Call today or request a free quote online.
            </p>
            <div class="cta-actions">
                <a href="tel:269-262-5055" class="cta-phone-btn">Call Now</a>
                <button class="cta-quote-btn" @click="openQuoteModal">Get My Free Estimate</button>
            </div>
        </section>

        <PageFooter />
    </div>
</template>

<script>
import ContactForm from '@/components/ContactForm.vue';
import PageFooter from '@/components/PageFooter.vue';
import { upsertJsonLd, removeJsonLd, BASE_URL } from '@/utils/seo';

export default {
    name: 'GaylordPage',
    components: {
        ContactForm,
        PageFooter
    },
    data() {
        return {
            services: [
                {
                    title: 'Roof Repair in Gaylord',
                    description: 'Fast roof repairs for leaks, missing shingles, flashing issues, storm damage, and aging roofing systems.'
                },
                {
                    title: 'Roof Replacement',
                    description: 'Full roof replacement options for homeowners ready to upgrade protection, curb appeal, and long-term value.'
                },
                {
                    title: 'Fascia & Soffit',
                    description: 'Fascia and soffit installation and repair to help defend your roofline from moisture and ventilation problems.'
                },
                {
                    title: 'Gutter Services',
                    description: 'Gutter replacement and drainage improvements that help move water away from your roof, siding, and foundation.'
                },
                {
                    title: 'Siding Services',
                    description: 'Exterior siding solutions that improve weather resistance, curb appeal, and overall home performance.'
                },
                {
                    title: 'Free Roof Inspections',
                    description: 'Straightforward inspections for homeowners in Gaylord who want answers before committing to a repair or replacement.'
                }
            ],
            benefits: [
                'Locally Owned and Operated',
                'Licensed and insured for your protection',
                'Financing Options Available',
                'High-quality materials and workmanship',
                'Free estimates and competitive pricing',
                'Roofing repair services available',
                'Customer satisfaction guaranteed'
            ],
            galleryLinks: [
                {
                    to: '/repair-projects',
                    title: 'Roof Repair Projects',
                    description: 'Browse examples of roof repair work and emergency repair results.'
                },
                {
                    to: '/roofing-projects',
                    title: 'Roof Replacement Projects',
                    description: 'See complete roofing projects and replacement work from across Michigan.'
                },
                {
                    to: '/fascia-soffit',
                    title: 'Fascia & Soffit',
                    description: 'Review fascia and soffit work that protects the roof edge and attic ventilation.'
                },
                {
                    to: '/gutter-services',
                    title: 'Gutter Services',
                    description: 'Learn more about gutters and water-management services for Michigan homes.'
                }
            ],
            faqs: [
                {
                    question: 'Do you offer emergency roof repair in Gaylord, Michigan?',
                    answer: 'Yes. If your home has a leak, missing shingles, or storm-related damage, call us at 269-262-5055 for fast help and guidance on the next steps.'
                },
                {
                    question: 'How much does roof repair or replacement cost in Gaylord?',
                    answer: 'The cost depends on the size of the roof, the materials involved, the amount of damage, and the complexity of the project. We provide free estimates so you can get a clear quote for your home.'
                },
                {
                    question: 'What roofing materials do you recommend for Gaylord homes?',
                    answer: 'For Gaylord weather, we often recommend durable architectural shingles and quality ventilation components that hold up well against snow, wind, and freeze-thaw cycles.'
                },
                {
                    question: 'Can you help with more than just roof repair in Gaylord?',
                    answer: 'Yes. In addition to roof repair and roof replacement, we also help with fascia, soffit, gutters, siding, and inspections so you can address multiple exterior issues with one team.'
                }
            ]
        };
    },
    methods: {
        openQuoteModal() {
            const contactForm = this.$refs.contactForm;
            if (contactForm && typeof contactForm.openModal === 'function') {
                contactForm.openModal();
            }
        }
    },
    mounted() {
        upsertJsonLd('gaylord-service', {
            '@context': 'https://schema.org',
            '@type': 'Service',
            name: 'Gaylord Roof Repair and Roofing Services',
            serviceType: [
                'Roof Repair',
                'Roof Replacement',
                'Fascia and Soffit Services',
                'Gutter Services',
                'Siding Services',
                'Roof Inspection'
            ],
            areaServed: {
                '@type': 'City',
                name: 'Gaylord',
                containedInPlace: {
                    '@type': 'State',
                    name: 'Michigan'
                }
            },
            provider: {
                '@type': 'RoofingContractor',
                name: 'Homes Roofing Co LLC',
                url: BASE_URL,
                telephone: '+1-269-262-5055'
            },
            offers: {
                '@type': 'Offer',
                availability: 'https://schema.org/InStock',
                priceCurrency: 'USD',
                description: 'Free estimates for roofing services in Gaylord, Michigan.'
            },
            description: 'Roof repair, roof replacement, gutter, fascia, soffit, siding, and inspection services for homeowners in Gaylord, Michigan.'
        });

        upsertJsonLd('gaylord-faq', {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: this.faqs.map((item) => ({
                '@type': 'Question',
                name: item.question,
                acceptedAnswer: {
                    '@type': 'Answer',
                    text: item.answer
                }
            }))
        });
    },
    beforeUnmount() {
        removeJsonLd('gaylord-service');
        removeJsonLd('gaylord-faq');
    }
};
</script>

<style scoped>
* {
    box-sizing: border-box;
}

.gaylord-page {
    margin-top: 93px;
    min-height: 100vh;
    background: linear-gradient(135deg, #002655 0%, #1a3a5c 50%, #4a7ba7 100%);
    color: white;
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

.floatingContact {
    z-index: 1000;
}

.floating-request-btn,
.floatingContact :deep(.contact-button) {
    position: fixed;
    bottom: max(16px, env(safe-area-inset-bottom));
    right: max(16px, env(safe-area-inset-right));
    background: linear-gradient(145deg, #a10d10 0%, #d3181c 100%);
    color: white;
    border: 2px solid rgba(241, 196, 15, 0.95);
    border-radius: 999px;
    min-width: 176px;
    max-width: 220px;
    height: 56px;
    padding: 0 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.95rem;
    font-weight: 800;
    letter-spacing: 0.35px;
    text-transform: uppercase;
    line-height: 1;
    white-space: nowrap;
    gap: 0;
    cursor: pointer;
    box-shadow: 0 10px 24px rgba(0, 0, 0, 0.4), 0 0 0 0 rgba(241, 196, 15, 0.55);
    transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
    animation: ctaAttention 5.5s cubic-bezier(0.4, 0, 0.2, 1) infinite;
    z-index: 1000;
}

.floating-request-btn:hover,
.floatingContact :deep(.contact-button:hover) {
    transform: translateY(-2px);
    background: linear-gradient(145deg, #b41013 0%, #e01f24 100%);
    box-shadow: 0 14px 28px rgba(0, 0, 0, 0.45), 0 0 0 6px rgba(241, 196, 15, 0.14);
}

.floating-request-btn:active,
.floatingContact :deep(.contact-button:active) {
    transform: translateY(0);
}

.floating-request-btn:focus-visible,
.floatingContact :deep(.contact-button:focus-visible) {
    outline: 3px solid #f1c40f;
    outline-offset: 2px;
}

.floatingContact :deep(.financing-button) {
    display: none;
}

@media (max-width: 500px) {
    .floating-request-btn,
    .floatingContact :deep(.contact-button) {
        min-width: 160px;
        height: 52px;
        font-size: 0.86rem;
        padding: 0 14px;
    }
}

@keyframes ctaAttention {
    0%, 72%, 100% {
        box-shadow: 0 10px 24px rgba(0, 0, 0, 0.4), 0 0 0 0 rgba(241, 196, 15, 0.55);
        transform: translateY(0);
    }
    10% {
        box-shadow: 0 12px 28px rgba(0, 0, 0, 0.45), 0 0 0 10px rgba(241, 196, 15, 0);
        transform: translateY(-1px);
    }
    18% {
        box-shadow: 0 12px 28px rgba(0, 0, 0, 0.45), 0 0 0 8px rgba(241, 196, 15, 0);
        transform: translateY(0);
    }
}

@media (prefers-reduced-motion: reduce) {
    .floating-request-btn,
    .floatingContact :deep(.contact-button) {
        transition: none;
        animation: none;
    }
}

.hero-section,
.content-section {
    max-width: 1080px;
    margin: 0 auto;
    padding: 30px;
    text-align: center;
}

.hero-section {
    padding-top: 70px;
}

.eyebrow {
    font-size: clamp(0.7rem, 1vw + 0.3rem, 0.875rem);
    color: #f1c40f;
    text-transform: uppercase;
    letter-spacing: 3px;
    font-weight: 700;
    margin-bottom: 15px;
}

h1 {
    font-size: clamp(1.75rem, 3.5vw + 0.5rem, 3rem);
    color: #f1c40f;
    margin: 0 0 25px;
    text-shadow: 3px 3px 6px rgba(0, 0, 0, 0.7);
}

h2 {
    font-size: clamp(1.35rem, 2.5vw + 0.5rem, 2.2rem);
    color: #f1c40f;
    margin: 0 0 20px;
    padding-bottom: 10px;
    border-bottom: 3px solid #f1c40f;
    display: inline-block;
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
}

h3 {
    color: #f1c40f;
    margin-bottom: 15px;
    font-size: clamp(1.05rem, 1.5vw + 0.2rem, 1.3rem);
}

.hero-intro,
.content-section p,
.section-intro {
    font-size: clamp(0.95rem, 1vw + 0.5rem, 1.12rem);
    line-height: 1.8;
    color: #e8e8e8;
}

.hero-intro {
    max-width: 920px;
    margin: 0 auto 25px;
    padding: 25px;
    background: rgba(255, 255, 255, 0.05);
    border-left: 5px solid #f1c40f;
    border-radius: 8px;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
}

.hero-actions,
.cta-actions {
    display: flex;
    justify-content: center;
    gap: 18px;
    flex-wrap: wrap;
    margin: 25px 0 30px;
}

.cta-phone-btn,
.cta-quote-btn {
    display: inline-block;
    padding: 14px 28px;
    border-radius: 8px;
    font-size: clamp(0.875rem, 1vw + 0.4rem, 1.05rem);
    font-weight: bold;
    text-decoration: none;
    transition: all 0.3s ease;
    cursor: pointer;
    border: none;
    background-color: #8d0808;
    color: #fff;
}

.cta-phone-btn:hover,
.cta-quote-btn:hover {
    background-color: #b30f12;
    transform: translateY(-2px);
}

.trust-points {
    list-style: none;
    padding: 0;
    margin: 0 auto;
    max-width: 900px;
    width: 100%;
}

.benefits-list {
    list-style: none !important;
    padding: 0 !important;
    margin: 0 !important;
    width: 100% !important;
    max-width: 100% !important;
    display: grid !important;
    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
    gap: 12px !important;
    text-align: left !important;
}

.trust-points li,
.benefits-list li {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 12px;
    padding: clamp(14px, 2.5vw, 18px) clamp(14px, 3vw, 20px);
    background: rgba(255, 255, 255, 0.07);
    border-radius: 10px;
    border-left: 4px solid #f1c40f;
    color: #f5f5f5;
    font-size: clamp(0.95rem, 1.8vw + 0.3rem, 1.05rem);
    line-height: 1.6;
}

.trust-points li::before,
.benefits-list li::before {
    content: "✓";
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    min-width: 24px;
    border-radius: 50%;
    background: #f1c40f;
    color: #1a1a1a;
    font-size: 0.8rem;
    font-weight: 900;
    margin-top: 1px;
}

@media (max-width: 600px) {
    .trust-points,
    .benefits-list {
        padding: 0 4px;
    }

    .benefits-list {
        grid-template-columns: 1fr !important;
    }

    .trust-points li,
    .benefits-list li {
        margin-bottom: 0;
        padding: 14px 16px;
        gap: 10px;
    }
}

.service-grid,
.link-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 20px;
    margin-top: 25px;
}

.service-card,
.link-card,
.faq-item,
.weather-section {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 10px;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25);
}

.service-card,
.link-card,
.faq-item {
    padding: 24px;
}

.service-card p,
.link-card p,
.faq-item p {
    margin: 0;
}

.link-card {
    text-decoration: none;
    color: inherit;
    transition: transform 0.3s ease, background 0.3s ease;
}

.link-card:hover {
    transform: translateY(-4px);
    background: rgba(241, 196, 15, 0.12);
}

.link-card span {
    display: inline-block;
    margin-top: 15px;
    color: #f1c40f;
    font-weight: bold;
}

.weather-section {
    padding: 30px;
}

.faq-section {
    display: grid;
    gap: 18px;
    margin-top: 25px;
}

.cta-section {
    padding-bottom: 60px;
}

@media (max-width: 880px) {
    .gaylord-page {
        margin-top: 168px;
    }



    .hero-section,
    .content-section {
        padding: 24px 20px;
    }
}
</style>
